import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import WorkRequested from "./workRequested";
import Notes from "./notes";
import { VscAccount } from "react-icons/vsc";
import axios from "axios";
import "react-datepicker/dist/react-datepicker.css";

const CustomDatePickerInput = ({ value, onClick }) => (
  <div
    onClick={onClick}
    style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
  >
    <input
      type="text"
      value={value}
      placeholder="Select Date"
      readOnly
      style={{
        width: "100%",
        padding: "8px",
        fontSize: "14px",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        backgroundColor: "#fff",
        cursor: "pointer",
      }}
    />
    <span style={{ marginLeft: "-30px", pointerEvents: "none" }}>📅</span>
  </div>
);

const PatientDetails = ({ account, onDataChange, formErrors, initialCaseData }) => {
  const [formData, setFormData] = useState({
    birth_date: new Date(),
    first_name: "",
    last_name: "",
    patientChart: "",
    selectedProvider: null,
    selectedGender: null,
    workRequested: {},
    notes: {},
  });

  const [contacts, setContacts] = useState([]);
  const [providers, setProviders] = useState([]); // For lab-specific providers
  const [loading, setLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');

  const genders = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "transgender", label: "Transgender" },
  ];

  // Get labId and userType from localStorage
  useEffect(() => {
    const getUserInfo = () => {
      const storedLabId = localStorage.getItem('userLabId');
      const storedUserType = localStorage.getItem('userType');
      
      console.log('PatientDetails - Loaded from localStorage:');
      console.log('LabId:', storedLabId);
      console.log('UserType:', storedUserType);
      
      if (storedLabId) {
        setLabId(storedLabId);
      }
      
      if (storedUserType) {
        setUserType(storedUserType);
      } else if (storedLabId) {
        setUserType('lab'); // Default to lab if labId exists
      }
    };
    
    getUserInfo();
  }, []);

  // Fetch contacts when account changes
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!account) {
          console.log("No account selected, skipping contacts fetch");
          setContacts([]);
          setLoading(false);
          return;
        }

        console.log(`Fetching contacts for account: ${account}`);
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/contacts/${account}`
        );

        const contactsData = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];
        
        console.log(`Found ${contactsData.length} contacts`);
        setContacts(contactsData);
        
        if (contactsData.length === 0) {
          console.log("No contacts found for this account");
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Failed to load providers. Please try again later.");
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [account]);

  // Fetch lab-specific providers when labId is available
  useEffect(() => {
    const fetchProviders = async () => {
      if (!labId && userType === 'lab') {
        console.log("No labId available for fetching lab providers");
        return;
      }

      try {
        setProvidersLoading(true);
        console.log(`Fetching providers for:`, { userType, labId });

        let providersData = [];

        if (userType === 'lab' && labId) {
          // For lab users: fetch lab-specific providers
          try {
            const response = await axios.get(
              `https://hms.backend.hiragroupcloud.com/api/providers/lab-providers/${labId}`
            );
            console.log('Lab providers response:', response.data);
            
            if (response.data.success && response.data.data) {
              providersData = response.data.data;
              console.log(`Found ${providersData.length} lab providers`);
            }
          } catch (labError) {
            console.error("Error fetching lab providers:", labError);
            // Fallback to all providers if lab-specific fails
            const allProvidersResponse = await axios.get(
              "https://hms.backend.hiragroupcloud.com/api/providers/providers"
            );
            if (allProvidersResponse.data.success && allProvidersResponse.data.data) {
              providersData = allProvidersResponse.data.data;
              console.log(`Fell back to ${providersData.length} all providers`);
            }
          }
        } else {
          // For admin users or if no labId: fetch all providers
          const response = await axios.get(
            "https://hms.backend.hiragroupcloud.com/api/providers/providers"
          );
          console.log('All providers response:', response.data);
          
          if (response.data.success && response.data.data) {
            providersData = response.data.data;
            console.log(`Found ${providersData.length} all providers`);
          }
        }

        setProviders(providersData);
      } catch (error) {
        console.error("Error fetching providers:", error);
        setProviders([]);
      } finally {
        setProvidersLoading(false);
      }
    };

    fetchProviders();
  }, [labId, userType]);

  // Prefill form data if initialCaseData is provided
  useEffect(() => {
    if (initialCaseData) {
      console.log("Prefilling form with case data:", initialCaseData);
      setFormData((prev) => ({
        ...prev,
        first_name: initialCaseData.first_name || "",
        last_name: initialCaseData.last_name || "",
        patientChart: initialCaseData.patientChart || "",
        birth_date: initialCaseData.birth_date ? new Date(initialCaseData.birth_date) : new Date(),
      }));
    }
  }, [initialCaseData]);

  // Transform contacts into provider options for Select component
  const contactProviderOptions = React.useMemo(() => {
    const options = contacts.map((contact) => ({
      value: contact._id,
      label: `${contact.firstName} ${contact.lastName}`,
      originalData: contact,
    }));
    
    console.log(`Generated ${options.length} contact provider options`);
    return options;
  }, [contacts]);

  // Transform lab providers into options for Select component
  const labProviderOptions = React.useMemo(() => {
    const options = providers.map((provider) => ({
      value: provider._id,
      label: `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || provider.name || 'Unnamed Provider',
      originalData: provider,
    }));
    
    console.log(`Generated ${options.length} lab provider options`);
    return options;
  }, [providers]);

  // Combine both contact and lab providers
  const allProviderOptions = React.useMemo(() => {
    const combined = [...contactProviderOptions, ...labProviderOptions];
    
    // Remove duplicates based on label
    const uniqueOptions = combined.filter((option, index, self) =>
      index === self.findIndex((t) => t.label === option.label)
    );
    
    console.log(`Combined providers: ${uniqueOptions.length} unique options`);
    return uniqueOptions;
  }, [contactProviderOptions, labProviderOptions]);

  // Set selectedProvider based on initialCaseData.provider_name after options are available
  useEffect(() => {
    if (initialCaseData && initialCaseData.provider_name && allProviderOptions.length > 0) {
      console.log("Looking for provider in options:", initialCaseData.provider_name);
      const matchingProvider = allProviderOptions.find(
        (option) => option.label === initialCaseData.provider_name
      );
      if (matchingProvider) {
        console.log("Found matching provider:", matchingProvider);
        setFormData((prev) => ({ ...prev, selectedProvider: matchingProvider }));
      } else {
        console.log("No matching provider found for:", initialCaseData.provider_name);
      }
    }
  }, [initialCaseData, allProviderOptions]);

  // Notify parent component when form data changes
  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProviderChange = (selectedProvider) => {
    setFormData((prev) => ({ ...prev, selectedProvider }));
  };

  const handleGenderChange = (selectedGender) => {
    setFormData((prev) => ({ ...prev, selectedGender }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, birth_date: date }));
  };

  const handleNotesDataChange = (data) => {
    setFormData((prev) => ({ ...prev, notes: data }));
  };

  const handleWorkRequestedDataChange = (data) => {
    setFormData((prev) => ({ ...prev, workRequested: data }));
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    fontSize: "14px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    marginBottom: "10px",
    backgroundColor: "#fff",
  };

  const labelStyle = {
    fontWeight: "bold",
    marginBottom: "5px",
    display: "block",
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      padding: "2px",
      fontSize: "14px",
      borderRadius: "4px",
      borderColor: "#ced4da",
      marginBottom: "10px",
      minHeight: "38px",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? "#004aad" : isFocused ? "#e6f0ff" : "white",
      color: isSelected ? "white" : "black",
    }),
  };

  const isLoading = loading || providersLoading;
  const combinedLoadingText = isLoading ? "Loading data..." : null;

  return (
    <div>
      <div className="main-div">
        <h3
          style={{
            marginTop: "10px",
            backgroundColor: "white",
            color: "#004aad",
          }}
        >
          <VscAccount style={{ marginRight: "5px", color: "#004aad" }} />
          Patient Details
        </h3>
        <div
          style={{
            border: "1px solid #ccc",
            padding: "20px",
            borderTop: "3px solid #004aad",
            backgroundColor: "#f0f0f0",
            width: "100%",
            margin: 0,
          }}
        >
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            {/* Left Section */}
            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>
                Provider <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                options={allProviderOptions}
                value={formData.selectedProvider}
                onChange={handleProviderChange}
                styles={selectStyles}
                placeholder={
                  isLoading ? "Loading providers..." : "Select Provider"
                }
                isLoading={isLoading}
                isDisabled={isLoading}
                className={formErrors.provider ? "is-invalid" : ""}
                noOptionsMessage={() =>
                  error ? error : "No providers available"
                }
              />
              {formErrors.provider && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formErrors.provider}
                </div>
              )}

              <label style={labelStyle}>First Name</label>
              <input
                type="text"
                value={formData.first_name}
                name="first_name"
                onChange={handleFormChange}
                style={inputStyle}
              />

              <label style={labelStyle}>
                Last Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={formData.last_name}
                name="last_name"
                onChange={handleFormChange}
                style={inputStyle}
                className={formErrors.lastName ? "is-invalid" : ""}
              />
              {formErrors.lastName && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formErrors.lastName}
                </div>
              )}
            </div>

            {/* Right Section */}
            <div style={{ flex: "1 1 45%" }}>
              <label style={labelStyle}>Gender</label>
              <Select
                options={genders}
                value={formData.selectedGender}
                onChange={handleGenderChange}
                styles={selectStyles}
                placeholder="Select Gender"
              />

              <label style={labelStyle}>Patient Chart</label>
              <input
                type="text"
                value={formData.patientChart}
                name="patientChart"
                onChange={handleFormChange}
                style={inputStyle}
                className={formErrors.patientChart ? "is-invalid" : ""}
              />
              {formErrors.patientChart && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {formErrors.patientChart}
                </div>
              )}

              <label style={labelStyle}>Birth Date</label>
              <DatePicker
                selected={formData.birth_date}
                onChange={handleDateChange}
                customInput={<CustomDatePickerInput />}
                name="birth_date"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>
          </div>
        </div>

        <br />
        {/* Pass labId to WorkRequested component for procedures */}
        <WorkRequested
          onFormSubmit={handleWorkRequestedDataChange}
          formErrors={formErrors}
          account={account}
          labId={labId}
          userType={userType}
        />
        <br />
        <Notes onDataChange={handleNotesDataChange} />
      </div>
    </div>
  );
};

export default PatientDetails;